import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, useMemo } from 'react';
import { useTheme } from '@emotion/react';
import Fade from '@mui/material/Fade';
import PropTypes from 'prop-types';
import { StyledIconToolTipContentHeader, StyledIconToolTipContentLink, StyledIconTooltip } from '../IconTooltip/IconTooltip.styles.js';
import { StyledIconButtonTooltipContainer, StyledIconButton } from './Button.styles.js';

var _excluded = ["children", "onClick", "classes", "disabled", "color", "size", "hoverColor", "padding", "tooltipProps"];
var defaultTooltipProps = {
  header: '',
  content: '',
  link: '',
  placement: 'top-start',
  withoutMaxWidth: false,
  disableFocusListener: true
};
var IconButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
    onClick = _ref.onClick,
    classes = _ref.classes,
    disabled = _ref.disabled,
    color = _ref.color,
    size = _ref.size,
    hoverColor = _ref.hoverColor,
    padding = _ref.padding,
    tooltipPropsProp = _ref.tooltipProps,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var tooltipProps = _objectSpread2(_objectSpread2({}, defaultTooltipProps), tooltipPropsProp);
  var header = tooltipProps.header,
    content = tooltipProps.content,
    link = tooltipProps.link,
    placement = tooltipProps.placement,
    withoutMaxWidth = tooltipProps.withoutMaxWidth,
    disableFocusListener = tooltipProps.disableFocusListener;
  var defaultColor = color || theme.customColors.grey[600];
  var defaultHoverColor = hoverColor || theme.palette.secondary.main;
  var renderContent = useMemo(function () {
    if (!header && !content && !link) {
      return '';
    }
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, header && /*#__PURE__*/React__default.createElement(StyledIconToolTipContentHeader, null, header), content, link && /*#__PURE__*/React__default.createElement(StyledIconToolTipContentLink, null, link));
  }, [header, content, link]);
  var handleClick = function handleClick(e) {
    onClick(e);
  };
  var renderButton = function renderButton() {
    return /*#__PURE__*/React__default.createElement(StyledIconButton, _extends({
      ref: ref,
      onClick: handleClick,
      color: defaultColor,
      hoverColorIconButton: defaultHoverColor,
      classes: classes,
      disabled: disabled,
      fontSize: size,
      padding: padding
    }, otherProps), children);
  };
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, !content ? renderButton() : /*#__PURE__*/React__default.createElement(StyledIconTooltip, _extends({
    title: renderContent,
    placement: placement,
    TransitionComponent: Fade,
    withoutMaxWidth: withoutMaxWidth,
    disableFocusListener: disableFocusListener,
    iconSize: size
  }, tooltipProps), /*#__PURE__*/React__default.createElement(StyledIconButtonTooltipContainer, null, renderButton())));
});
IconButton.propTypes = {
  /**
   * The icon of the button.
   */
  children: PropTypes.node,
  /**
   * Override or extend the styles applied to the component. See CSS API below for more details.
   */
  classes: PropTypes.objectOf(PropTypes.shape({})),
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  color: PropTypes.string,
  /**
   * hover color.
   */
  hoverColor: PropTypes.string,
  /**
   * The size of the button. small is equivalent to the dense button styling.
   */
  size: PropTypes.number,
  /**
  /**
   * If true, the button will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If true, the ripple effect will be disabled.
   * ⚠️ Without a ripple there is no styling for :focus-visible by default.
   * Be sure to highlight the element by applying separate styles with the focusVisibleClassName.
   */
  disableRipple: PropTypes.bool,
  /**
   * onClickEvent
   */
  onClick: PropTypes.func,
  /**
   * The padding of icon.
   */
  padding: PropTypes.number,
  tooltipProps: PropTypes.shape({
    header: PropTypes.node,
    content: PropTypes.node,
    link: PropTypes.node,
    placement: PropTypes.oneOf(['bottom-end', 'bottom-start', 'bottom', 'left-end', 'left-start', 'left', 'right-end', 'right-start', 'right', 'top-end', 'top-start', 'top']),
    withoutMaxWidth: PropTypes.bool,
    disableFocusListener: PropTypes.bool
  })
};
IconButton.defaultProps = {
  children: null,
  classes: null,
  color: '',
  hoverColor: '',
  size: 24,
  disabled: false,
  disableRipple: true,
  onClick: function onClick() {},
  padding: 0,
  tooltipProps: {}
};
var IconButton$1 = IconButton;

export { IconButton$1 as default };
